// Controls
import { Button, Field, Input, mergeClasses, Radio, RadioGroup, Spinner, Text, Textarea } from "@fluentui/react-components";
import { ChevronDown, ChevronRight } from "@ms/planner-generic-controls/lib/local/icons";
import { SensitivityLabelPicker } from "../sensitivityLabelPicker/SensitivityLabelPicker";
import { ModernGroup } from "@ms/planner-models/lib/local/client/modernGroup/ModernGroup";
import { SensitivityLabelGroupPrivacy } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { SensitivityLabel } from "@ms/planner-models/lib/local/client/tenant/SensitivityLabel";
import { GroupVisibilityMode } from "@ms/planner-models/lib/local/service/graph/enumerations/GroupVisibilityMode";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { useGroupSettingsFormStyles } from "./GroupSettingsForm.styles";
// Utilities
import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import * as React from "react";
/**
 * GroupSettingsForm component displays group-specific settings including
 * group name, description, privacy, and sensitivity label
 */ export const GroupSettingsForm = (props)=>{
    const { modernGroup, isGroupOwner, sensitivityLabels, onUpdateGroupSettings, isUpdatingGroupSettings, isCollapsible = true, currentSensitivityLabelId, onStateChange } = props;
    const styles = useGroupSettingsFormStyles();
    // Track the group ID to detect when we need to reset form state
    const [trackedGroupId, setTrackedGroupId] = React.useState(undefined);
    // Local state for group settings
    const [isGroupSettingsExpanded, setIsGroupSettingsExpanded] = React.useState(true);
    const [currentGroupName, setCurrentGroupName] = React.useState("");
    const [currentGroupDescription, setCurrentGroupDescription] = React.useState("");
    const [currentIsPublic, setCurrentIsPublic] = React.useState(false);
    const [currentSensitivityLabel, setCurrentSensitivityLabel] = React.useState(null);
    const [isGroupNameEmpty, setIsGroupNameEmpty] = React.useState(false);
    // Reset form state when modernGroup changes (getDerivedStateFromProps pattern)
    // This avoids useEffect with setState which causes multi-pass renders
    const groupId = modernGroup?.id;
    if (groupId !== trackedGroupId) {
        setTrackedGroupId(groupId);
        if (modernGroup) {
            setCurrentGroupName(modernGroup.displayName || "");
            setCurrentGroupDescription(modernGroup.description || "");
            setCurrentIsPublic(modernGroup.visibility === GroupVisibilityMode.Public);
            setCurrentSensitivityLabel(modernGroup.assignedSensitivityLabelId || currentSensitivityLabelId || null);
            setIsGroupNameEmpty(false);
        }
    }
    /**
     * Check if settings have changed compared to the original modernGroup values
     * @returns true if any form field differs from the original group settings
     */ const hasGroupSettingsChanged = React.useMemo(()=>{
        if (!modernGroup) {
            return false;
        }
        const nameChanged = currentGroupName !== (modernGroup.displayName || "");
        const descriptionChanged = currentGroupDescription !== (modernGroup.description || "");
        const visibilityChanged = currentIsPublic !== (modernGroup.visibility === GroupVisibilityMode.Public);
        const sensitivityChanged = currentSensitivityLabel !== (modernGroup.assignedSensitivityLabelId || null);
        return nameChanged || descriptionChanged || visibilityChanged || sensitivityChanged;
    }, [
        modernGroup,
        currentGroupName,
        currentGroupDescription,
        currentIsPublic,
        currentSensitivityLabel
    ]);
    /**
     * Handle save button click.
     * Validates that there are changes, name is not empty, and update is not in progress.
     */ const handleSaveGroupSettings = React.useCallback(()=>{
        if (onUpdateGroupSettings && hasGroupSettingsChanged && !isUpdatingGroupSettings && !isGroupNameEmpty) {
            onUpdateGroupSettings({
                displayName: currentGroupName,
                description: currentGroupDescription,
                visibility: currentIsPublic ? GroupVisibilityMode.Public : GroupVisibilityMode.Private,
                assignedSensitivityLabelId: currentSensitivityLabel || ""
            });
        }
    }, [
        onUpdateGroupSettings,
        hasGroupSettingsChanged,
        isUpdatingGroupSettings,
        isGroupNameEmpty,
        currentGroupName,
        currentGroupDescription,
        currentIsPublic,
        currentSensitivityLabel
    ]);
    // Use a ref to always have the latest save handler without causing effect re-runs
    const saveHandlerRef = React.useRef(handleSaveGroupSettings);
    saveHandlerRef.current = handleSaveGroupSettings;
    // Stable save handler that always calls the latest version
    const stableSaveHandler = React.useCallback(()=>{
        saveHandlerRef.current();
    }, []);
    // Track previous notification values to avoid redundant parent callbacks
    // This replaces useEffect to avoid multi-pass renders while still notifying on changes
    const prevNotificationRef = React.useRef({
        hasChanges: false,
        isNameEmpty: false,
        onStateChange: undefined
    });
    // Notify parent of state changes - called via useLayoutEffect to run synchronously after render
    // This ensures parent receives updates after DOM mutations but before paint
    React.useLayoutEffect(()=>{
        if (onStateChange && (prevNotificationRef.current.hasChanges !== hasGroupSettingsChanged || prevNotificationRef.current.isNameEmpty !== isGroupNameEmpty || prevNotificationRef.current.onStateChange !== onStateChange)) {
            prevNotificationRef.current = {
                hasChanges: hasGroupSettingsChanged,
                isNameEmpty: isGroupNameEmpty,
                onStateChange
            };
            onStateChange(hasGroupSettingsChanged, stableSaveHandler, isGroupNameEmpty);
        }
    }, [
        hasGroupSettingsChanged,
        isGroupNameEmpty,
        onStateChange,
        stableSaveHandler
    ]);
    /**
     * Check if privacy is disabled by the current sensitivity label.
     * Privacy can be enforced by either a top-level label or a sublabel.
     * @returns true if the selected sensitivity label enforces a specific privacy setting
     */ const isPrivacyDisabledBySensitivityLabel = React.useMemo(()=>{
        if (!currentSensitivityLabel || !sensitivityLabels) {
            return false;
        }
        const label = SensitivityLabel.getSensitivityLabel(currentSensitivityLabel, sensitivityLabels);
        return !!label?.groupPrivacy;
    }, [
        currentSensitivityLabel,
        sensitivityLabels
    ]);
    /** Whether the current user can modify the sensitivity label (must be group owner with a loaded group) */ const canUpdateSettings = isGroupOwner && modernGroup != null;
    /**
     * Get the display title for the currently selected sensitivity label.
     * Uses the standard "Parent / Sublabel" format for sublabels.
     * @returns The formatted display name of the current sensitivity label, or empty string if none selected
     */ const getCurrentSensitivityLabelTitle = React.useMemo(()=>SensitivityLabel.getFullSensitivityTitle(currentSensitivityLabel || "", sensitivityLabels || null), [
        currentSensitivityLabel,
        sensitivityLabels
    ]);
    /**
     * Handle privacy radio button change.
     * Ignores changes if privacy is controlled by a sensitivity label.
     * @param value - "public" or "private"
     */ const handleGroupPrivacyChange = React.useCallback((value)=>{
        if (isPrivacyDisabledBySensitivityLabel) {
            return;
        }
        setCurrentIsPublic(value === "public");
    }, [
        isPrivacyDisabledBySensitivityLabel
    ]);
    /**
     * Handle sensitivity label selection.
     * Also updates privacy if the selected label enforces a specific privacy setting.
     * @param labelId - The ID of the selected sensitivity label, or null to clear
     */ const handleSensitivityLabelSelected = React.useCallback((labelId)=>{
        setCurrentSensitivityLabel(labelId);
        // If the selected label enforces privacy, update privacy based on groupPrivacy
        if (labelId && sensitivityLabels) {
            const selectedLabel = SensitivityLabel.getSensitivityLabel(labelId, sensitivityLabels);
            if (!!selectedLabel?.groupPrivacy) {
                setCurrentIsPublic(selectedLabel.groupPrivacy === SensitivityLabelGroupPrivacy.Public);
            }
        }
    }, [
        sensitivityLabels
    ]);
    /** Validation error message for the group name field */ const groupNameError = isGroupNameEmpty ? Strings.Common_ValidatorRequiredMessage : undefined;
    return /*#__PURE__*/ React.createElement("div", {
        "data-testid": "group-settings-form",
        className: styles.groupSettingsSection
    }, isCollapsible ? /*#__PURE__*/ React.createElement(Button, {
        "data-testid": "group-settings-form-button",
        appearance: "transparent",
        onClick: ()=>setIsGroupSettingsExpanded(!isGroupSettingsExpanded),
        "aria-expanded": isGroupSettingsExpanded,
        "aria-controls": "group-settings-content",
        "aria-label": `${isGroupSettingsExpanded ? Strings.PlanSettings_CollapseAriaLabel : Strings.PlanSettings_ExpandAriaLabel} ${Strings.PlanSettings_GroupSettingsSectionTitle}`,
        className: styles.groupSettingsToggleButton
    }, isGroupSettingsExpanded ? /*#__PURE__*/ React.createElement(ChevronDown, {
        className: styles.groupSettingsToggleIcon
    }) : /*#__PURE__*/ React.createElement(ChevronRight, {
        className: styles.groupSettingsToggleIcon
    }), /*#__PURE__*/ React.createElement(Text, {
        as: "h3",
        className: styles.groupSettingsSectionTitle
    }, Strings.PlanSettings_GroupSettingsSectionTitle)) : /*#__PURE__*/ React.createElement(Text, {
        as: "h3",
        className: styles.groupSettingsSectionTitle
    }, Strings.PlanSettings_GroupSettingsSectionTitle), (!isCollapsible || isGroupSettingsExpanded) && /*#__PURE__*/ React.createElement("div", {
        id: isCollapsible ? "group-settings-content" : undefined,
        className: mergeClasses(styles.groupSettingsContent, isCollapsible && styles.groupSettingsContentIndented)
    }, !modernGroup ? /*#__PURE__*/ React.createElement("div", {
        className: styles.groupSettingsLoadingContainer,
        role: "status",
        "aria-live": "polite"
    }, /*#__PURE__*/ React.createElement(Spinner, {
        size: "small"
    }), /*#__PURE__*/ React.createElement(Text, {
        className: styles.groupSettingsLoadingText
    }, Strings.PlanSettings_LoadingGroupSettings)) : /*#__PURE__*/ React.createElement("div", {
        className: styles.groupSettingsFieldsContainer
    }, /*#__PURE__*/ React.createElement(Field, {
        label: /*#__PURE__*/ React.createElement(Text, {
            className: styles.fieldLabel
        }, Strings.PlanSettings_GroupNameLabel),
        required: true,
        validationMessage: groupNameError,
        validationState: isGroupNameEmpty ? "error" : "none"
    }, /*#__PURE__*/ React.createElement(Input, {
        value: currentGroupName,
        onChange: (e)=>{
            setCurrentGroupName(e.target.value);
            setIsGroupNameEmpty(isEmpty(trim(e.target.value)));
        },
        placeholder: Strings.PlanSettings_GroupNamePlaceholder,
        "aria-label": Strings.PlanSettings_GroupNameLabel,
        required: true,
        maxLength: ModernGroup.DisplayNameMaxLength,
        className: styles.groupInputStyle,
        disabled: !canUpdateSettings
    })), /*#__PURE__*/ React.createElement(Field, {
        label: /*#__PURE__*/ React.createElement(Text, {
            className: styles.fieldLabel
        }, Strings.PlanSettings_GroupDescriptionLabel)
    }, /*#__PURE__*/ React.createElement(Textarea, {
        value: currentGroupDescription,
        onChange: (e)=>setCurrentGroupDescription(e.target.value),
        placeholder: Strings.PlanSettings_GroupDescriptionPlaceholder,
        "aria-label": Strings.PlanSettings_GroupDescriptionLabel,
        rows: 2,
        resize: "vertical",
        maxLength: ModernGroup.DescriptionMaxLength,
        className: styles.groupInputStyle,
        disabled: !canUpdateSettings
    })), /*#__PURE__*/ React.createElement(Field, {
        label: /*#__PURE__*/ React.createElement(Text, {
            className: styles.fieldLabel
        }, Strings.PlanSettings_PrivacyLabel),
        className: styles.privacyField
    }, /*#__PURE__*/ React.createElement(RadioGroup, {
        "data-testid": "group-settings-form-radio-group",
        value: currentIsPublic ? "public" : "private",
        onChange: (_, data)=>handleGroupPrivacyChange(data.value),
        disabled: isPrivacyDisabledBySensitivityLabel || !canUpdateSettings,
        "aria-label": Strings.PlanSettings_PrivacyLabel
    }, /*#__PURE__*/ React.createElement(Radio, {
        "data-testid": "group-settings-form-radio-public",
        value: "public",
        label: `${Strings.PlanSettings_PublicOption} - ${Strings.PlanSettings_PublicOptionDescription}`
    }), /*#__PURE__*/ React.createElement(Radio, {
        "data-testid": "group-settings-form-radio-private",
        value: "private",
        label: `${Strings.PlanSettings_PrivateOption} - ${Strings.PlanSettings_PrivateOptionDescription}`
    })), isPrivacyDisabledBySensitivityLabel && /*#__PURE__*/ React.createElement(Text, {
        className: styles.privacyDisabledNote
    }, Strings.PlanSettings_PrivacyDisabledBySensitivityLabel)), /*#__PURE__*/ React.createElement("div", {
        className: styles.sensitivityLabelSection
    }, /*#__PURE__*/ React.createElement(SensitivityLabelPicker, {
        sensitivityLabelTitle: getCurrentSensitivityLabelTitle,
        userAccessibleTenantSensitivityLabels: sensitivityLabels || [],
        onSensitivitySelected: (label)=>handleSensitivityLabelSelected(label.id),
        canChangeSensitivity: canUpdateSettings,
        currentSensitivityLabelId: currentSensitivityLabel || ""
    })))));
};
